/**
 * Task-management constants shared by the backend (validation + seeding) and
 * the frontend (badges, pickers, defaults). Status and priority are stored as
 * `VarChar` in Postgres (not native enums) so the ladder can be tweaked
 * without a schema migration; these tuples are the canonical source of truth
 * that Zod validates against.
 */

/** Task urgency, highest → lowest. Drives the priority badge + default sort. */
export const TASK_PRIORITIES = ['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

/** Human labels for each priority. */
export const TASK_PRIORITY_DISPLAY: Record<TaskPriority, string> = {
  URGENT: 'Urgent',
  HIGH: 'High',
  NORMAL: 'Normal',
  LOW: 'Low',
};

/** Membership role within a single board. The CO is never a member row. */
export const BOARD_MEMBER_ROLES = ['OWNER', 'MEMBER'] as const;
export type BoardMemberRole = (typeof BOARD_MEMBER_ROLES)[number];

/**
 * The caller's effective relationship to a board, returned with every board
 * payload so the UI can show/hide owner-only controls. `CO_OVERRIDE` is the
 * standing role-based admin access the CO has on every board without being a
 * member row.
 */
export const BOARD_ACCESS_RELATIONS = ['CO_OVERRIDE', 'OWNER', 'MEMBER', 'NONE'] as const;
export type BoardAccessRelation = (typeof BOARD_ACCESS_RELATIONS)[number];

/**
 * Default status ladder seeded on every new board (Monday.com-style). Stored
 * per-board in `BoardStatus` so each board can rename / recolour / extend its
 * own ladder. Exactly one default-seeded status carries `isDone` so the
 * "done" rollups and My Work filters work out of the box.
 */
export interface DefaultBoardStatus {
  name: string;
  color: string;
  isDone: boolean;
}

export const DEFAULT_BOARD_STATUSES: readonly DefaultBoardStatus[] = [
  { name: 'Not started', color: '#c4c4c4', isDone: false },
  { name: 'Working on it', color: '#fdab3d', isDone: false },
  { name: 'Stuck', color: '#e2445c', isDone: false },
  { name: 'Done', color: '#00c875', isDone: true },
] as const;

/** Board-label palette — reused for the label colour picker. */
export const LABEL_COLORS: readonly string[] = [
  '#0e7490',
  '#047857',
  '#b45309',
  '#b91c1c',
  '#6d28d9',
  '#be185d',
  '#1d4ed8',
  '#4d7c0f',
  '#0f766e',
  '#7c3aed',
] as const;

/** My Work filter buckets. */
export const MY_WORK_FILTERS = ['open', 'done', 'overdue'] as const;
export type MyWorkFilter = (typeof MY_WORK_FILTERS)[number];

/** A 7-char hex colour (`#rrggbb`). Used for statuses + labels. */
export const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;
